import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PASSWORD_MAXIMUM_CHARACTERS, PASSWORD_MINIMUM_CHARACTERS } from 'App/constants/password';
import { logError } from 'App/services/faroService';
import Header from 'Pages/forgotPassword/components/header/Header';
import Error from 'Pages/updatePassword/components/error/Error';
import SubmitPassword from 'Pages/updatePassword/components/submitPassword/SubmitPassword';
import Success from 'Pages/updatePassword/components/success/Success';
import { TextFieldTypes } from 'Shared/components/design/textField/TextField';
import { useKeyDown } from 'Shared/hooks/useKeyDown';
import { isValidPassword } from 'Shared/services/inputValidationService';
import { navigateToSignInPage } from 'Shared/services/routingService';
import { updateUserPassword } from 'Shared/services/userActivation/api/idpApiService';

enum Slides {
  PASSWORD_SUBMIT,
  SUCCESS,
  ERROR,
}

type InputState = {
  fieldType  : TextFieldTypes;
  helperText?: string;
};

/**
 * Renders UpdatePassword slides
 */
function UpdatePassword(): JSX.Element {
  const { token }                                = useParams<{ token: string }>();
  const { t }                                    = useTranslation('updatePassword');
  const [inputPassword, setInputPassword]         = useState<string>('');
  const [inputConfirmPassword, setInputConfirmPassword] = useState<string>('');
  const [passwordState, setPasswordState]         = useState<InputState>({fieldType: TextFieldTypes.DEFAULT});
  const [confirmPasswordState, setConfirmPasswordState] = useState<InputState>({fieldType: TextFieldTypes.DEFAULT});
  const [apiError, setApiError]                  = useState<boolean>(false);
  const [isLoading, setIsLoading]                = useState<boolean>(false);
  const [slide, setSlide]                        = useState<Slides>(Slides.PASSWORD_SUBMIT);
  const [validPassword, setValidPassword]         = useState<boolean>(false);
  const [validConfirmPassword, setValidConfirmPassword] = useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch]      = useState<boolean>(false);

  /**
   * Check if token exists on mount
   */
  useEffect(() => {
    if (!token) {
      setSlide(Slides.ERROR);
      setApiError(true);
    }
  }, [token]);

  /**
   * Detecting Enter key press to submit form
   */
  useKeyDown(() => {
    if (validPassword && validConfirmPassword && passwordsMatch) {
      handlePasswordSubmit();
    }
  });

  /**
   * Validates the password on blur
   * @param newPassword
   */
  const handlePasswordBlur = (newPassword: string): void => {
    if (newPassword.length > 0) {
      if (!isValidPassword(newPassword)) {
        setPasswordState({
          fieldType : TextFieldTypes.ERROR,
          helperText: t('UpdatePassword--submitPassword-mustBeAtLeastXCharactersNoSpaces', {minimum: PASSWORD_MINIMUM_CHARACTERS, maximum: PASSWORD_MAXIMUM_CHARACTERS})
        });
        setValidPassword(false);
      } else {
        setPasswordState({fieldType: TextFieldTypes.DEFAULT});
        setValidPassword(true);
        if (inputConfirmPassword && newPassword !== inputConfirmPassword) {
          setConfirmPasswordState({
            fieldType : TextFieldTypes.ERROR,
            helperText: t('UpdatePassword--submitPassword-passwordsDoNotMatch')
          });
          setPasswordsMatch(false);
        } else if (inputConfirmPassword && newPassword === inputConfirmPassword) {
          setConfirmPasswordState({fieldType: TextFieldTypes.DEFAULT});
          setPasswordsMatch(true);
        }
      }
    } else {
      setPasswordState({fieldType: TextFieldTypes.DEFAULT});
      setValidPassword(false);
    }
  };

  /**
   * Password validation on change
   * Sets the password to the state if valid
   * @param newPassword
   */
  const handlePasswordChange = (newPassword: string): void => {
    setInputPassword(newPassword);

    if (isValidPassword(newPassword)) {
      setPasswordState({fieldType: TextFieldTypes.DEFAULT});
      setValidPassword(true);
      if (inputConfirmPassword && newPassword !== inputConfirmPassword) {
        setConfirmPasswordState({
          fieldType : TextFieldTypes.ERROR,
          helperText: t('UpdatePassword--submitPassword-passwordsDoNotMatch')
        });
        setPasswordsMatch(false);
      } else if (inputConfirmPassword && newPassword === inputConfirmPassword) {
        setConfirmPasswordState({fieldType: TextFieldTypes.DEFAULT});
        setPasswordsMatch(true);
      }
    }
  };

  /**
   * Validates the confirm password on blur
   * @param newConfirmPassword
   */
  const handleConfirmPasswordBlur = (newConfirmPassword: string): void => {
    if (newConfirmPassword.length > 0) {
      if (!isValidPassword(newConfirmPassword)) {
        setConfirmPasswordState({
          fieldType : TextFieldTypes.ERROR,
          helperText: t('UpdatePassword--submitPassword-mustBeAtLeastXCharactersNoSpaces', {minimum: PASSWORD_MINIMUM_CHARACTERS, maximum: PASSWORD_MAXIMUM_CHARACTERS})
        });
        setValidConfirmPassword(false);
        setPasswordsMatch(false);
      } else if (inputPassword && newConfirmPassword !== inputPassword) {
        setConfirmPasswordState({
          fieldType : TextFieldTypes.ERROR,
          helperText: t('UpdatePassword--submitPassword-passwordsDoNotMatch')
        });
        setValidConfirmPassword(true);
        setPasswordsMatch(false);
      } else {
        setConfirmPasswordState({fieldType: TextFieldTypes.DEFAULT});
        setValidConfirmPassword(true);
        setPasswordsMatch(true);
      }
    } else {
      setConfirmPasswordState({fieldType: TextFieldTypes.DEFAULT});
      setValidConfirmPassword(false);
      setPasswordsMatch(false);
    }
  };

  /**
   * Confirm password validation on change
   * Sets the confirm password to the state if valid
   * @param newConfirmPassword
   */
  const handleConfirmPasswordChange = (newConfirmPassword: string): void => {
    setInputConfirmPassword(newConfirmPassword);

    if (isValidPassword(newConfirmPassword)) {
      setValidConfirmPassword(true);
      if (inputPassword && newConfirmPassword !== inputPassword) {
        setConfirmPasswordState({
          fieldType : TextFieldTypes.ERROR,
          helperText: t('UpdatePassword--submitPassword-passwordsDoNotMatch')
        });
        setPasswordsMatch(false);
      } else if (inputPassword && newConfirmPassword === inputPassword) {
        setConfirmPasswordState({fieldType: TextFieldTypes.DEFAULT});
        setPasswordsMatch(true);
      }
    }
  };

  /**
   * Submits the password update to IDP
   */
  const handlePasswordSubmit = (): void => {
    if (isLoading || !token) {
      return;
    }

    setIsLoading(true);
    setApiError(false);

    updateUserPassword(token, inputPassword).then(() => {
      setSlide(Slides.SUCCESS);
      setIsLoading(false);
      setApiError(false);
    }).catch(error => {
      setSlide(Slides.ERROR);
      setApiError(true);
      setIsLoading(false);
      logError('updateUserPassword', error);
    });
  };

  /**
   * Generates header title according to slide
   */
  const getHeaderTitle = (): string => {
    switch (slide) {
      case Slides.PASSWORD_SUBMIT:
        return t('UpdatePassword--submitPassword-headerTitle');
      case Slides.SUCCESS:
        return t('UpdatePassword--success-headerTitle');
      case Slides.ERROR:
        return t('UpdatePassword--error-headerTitle');
    }
  };

  /**
   * Conditionally render slides
   */
  const renderSlides = (): JSX.Element | undefined => {
    switch (slide) {
      case Slides.PASSWORD_SUBMIT:
        return (
          <SubmitPassword
            passwordBlur            = {handlePasswordBlur}
            passwordCallback        = {handlePasswordChange}
            confirmPasswordBlur     = {handleConfirmPasswordBlur}
            confirmPasswordCallback = {handleConfirmPasswordChange}
            isButtonDisabled        = {!(validPassword && validConfirmPassword && passwordsMatch)}
            submitPasswordCallback  = {handlePasswordSubmit}
            passwordFieldType       = {passwordState.fieldType}
            passwordHelperText      = {passwordState.helperText}
            confirmPasswordFieldType = {confirmPasswordState.fieldType}
            confirmPasswordHelperText = {confirmPasswordState.helperText}
            isLoading               = {isLoading}
          />
        );
      case Slides.SUCCESS:
        return (
          <Success
            signInRedirect = {navigateToSignInPage}
          />
        );
      case Slides.ERROR:
        return <Error signInRedirect={navigateToSignInPage} />;
    }
  };

  return (
    <div>
      <Header headerTitle={getHeaderTitle()} isError={apiError} />
      {renderSlides()}
    </div>
  );
}

export default UpdatePassword;

